import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ApiError } from '@/api/client'
import { UnsavedChangesDialog } from '@/components/UnsavedChanges'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useRegime } from '@/features/company/regime'
import {
  createTaxRule,
  flattenCategories,
  listCategories,
  listAllProducts,
  type TaxRule,
} from '@/features/inventory/api'
import { useDirtyGuard } from '@/lib/dirtyGuard'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const SCOPES = ['product', 'category', 'catch_all'] as const
type Scope = (typeof SCOPES)[number]

const SCOPE_LABELS: Record<Scope, string> = {
  product: 'One product',
  category: 'A whole category',
  catch_all: 'Everything else (catch-all)',
}

const schema = z
  .object({
    name: z.string().trim().min(1, 'Enter a name').max(100, 'That name is too long'),
    scope: z.enum(SCOPES),
    product_id: z.string(),
    category_id: z.string(),
    rate: z
      .string()
      .trim()
      .min(1, 'Enter a rate')
      .refine((value) => Number.isFinite(Number(value)), { message: 'Enter a number' })
      .refine((value) => Number(value) >= 0 && Number(value) <= 100, {
        message: 'A rate is between 0 and 100',
      }),
    valid_from: z.string().trim().min(1, 'Enter a start date'),
    valid_to: z.string().trim(),
  })
  .refine((values) => values.scope !== 'product' || values.product_id, {
    message: 'Choose a product',
    path: ['product_id'],
  })
  .refine((values) => values.scope !== 'category' || values.category_id, {
    message: 'Choose a category',
    path: ['category_id'],
  })
  .refine((values) => !values.valid_to || values.valid_to > values.valid_from, {
    message: 'Must be after the start date',
    path: ['valid_to'],
  })

type FormValues = z.infer<typeof schema>

/**
 * Only creation lives here. A rate and its scope are immutable once set -- the only edit a
 * tax rule ever takes is closing its effective window, which the list screen does inline
 * with a "close" action rather than reopening this form. See `closeTaxRule`.
 */
export function TaxRuleFormDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const regime = useRegime()
  const [formError, setFormError] = useState<string | null>(null)

  const categories = useQuery({
    queryKey: ['categories'],
    queryFn: listCategories,
    retry: false,
    staleTime: 5 * 60_000,
  })
  const categoryOptions = flattenCategories(categories.data ?? [])

  const products = useQuery({
    queryKey: ['products', 'all'],
    queryFn: () => listAllProducts(),
    retry: false,
    staleTime: 60_000,
  })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      scope: 'catch_all',
      product_id: '',
      category_id: '',
      rate: '',
      valid_from: new Date().toISOString().slice(0, 10),
      valid_to: '',
    },
  })

  const scope = watch('scope')

  const guard = useDirtyGuard(open && isDirty)

  const close = () => {
    guard.attempt(() => {
      setFormError(null)
      reset()
      onClose()
    })
  }

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      createTaxRule({
        name: values.name,
        rate: values.rate,
        product_id: values.scope === 'product' ? values.product_id : null,
        category_id: values.scope === 'category' ? values.category_id : null,
        valid_from: values.valid_from,
        valid_to: values.valid_to || null,
      }),
    onSuccess: (saved: TaxRule) => {
      void queryClient.invalidateQueries({ queryKey: ['tax-rules'] })
      toast.success(`Tax rule "${saved.name}" added.`)
      reset()
      onClose()
    },
    onError: (error: unknown) => {
      setFormError(error instanceof ApiError ? error.message : 'Could not reach the server. Try again.')
    },
  })

  const onSubmit = handleSubmit((values) => {
    setFormError(null)
    return save.mutateAsync(values).catch(() => {
      /* surfaced in formError by onError */
    })
  })

  return (
    <>
      <Modal
        open={open}
        onClose={close}
        title="Add a tax rule"
        description="Overrides a product's own tax rate for whatever it applies to, from the start date on. Existing orders keep the rate they were priced at."
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" form="tax-rule-form" loading={isSubmitting}>
              Add rule
            </Button>
          </>
        }
      >
        <form id="tax-rule-form" onSubmit={onSubmit} noValidate className="space-y-4">
          {formError && <Alert tone="error">{formError}</Alert>}

          <Field label="Name" error={errors.name?.message}>
            {(props) => (
              <Input {...props} {...register('name')} autoComplete="off" placeholder="e.g. Hot beverages GST" />
            )}
          </Field>

          <Field label="Applies to" error={errors.scope?.message}>
            {(props) => (
              <select {...props} {...register('scope')} className={SELECT_CLASS}>
                {SCOPES.map((value) => (
                  <option key={value} value={value}>
                    {SCOPE_LABELS[value]}
                  </option>
                ))}
              </select>
            )}
          </Field>

          {scope === 'product' && (
            <Field label="Product" error={errors.product_id?.message}>
              {(props) => (
                <select {...props} {...register('product_id')} className={SELECT_CLASS}>
                  <option value="">Choose a product…</option>
                  {(products.data ?? []).map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.code} — {product.name}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          {scope === 'category' && (
            <Field label="Category" error={errors.category_id?.message}>
              {(props) => (
                <select {...props} {...register('category_id')} className={SELECT_CLASS}>
                  <option value="">Choose a category…</option>
                  {categoryOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          )}

          <Field
            label={`Rate (%)`}
            error={errors.rate?.message}
            hint={
              regime.rate_slabs
                ? `${regime.name} allows a fixed set of rates.`
                : 'Any rate up to 100, three decimals.'
            }
          >
            {(props) =>
              regime.rate_slabs ? (
                <select {...props} {...register('rate')} className={SELECT_CLASS}>
                  <option value="">Choose a rate…</option>
                  {regime.rate_slabs.map((slab) => (
                    <option key={slab} value={slab}>
                      {slab}%
                    </option>
                  ))}
                </select>
              ) : (
                <Input {...props} {...register('rate')} inputMode="decimal" className="tabular-nums" />
              )
            }
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Effective from" error={errors.valid_from?.message}>
              {(props) => <Input {...props} {...register('valid_from')} type="date" />}
            </Field>
            <Field label="Effective to" error={errors.valid_to?.message} hint="Optional.">
              {(props) => <Input {...props} {...register('valid_to')} type="date" />}
            </Field>
          </div>
        </form>
      </Modal>

      <UnsavedChangesDialog guard={guard} />
    </>
  )
}
